import { IconInfoCircle } from '@tabler/icons-react'
import VisualizationsBasicMap from '../visualization-charts/VisualizationsBasicMap'
import VisualizationsBasicBar from '../visualization-charts/VisualizationsBasicBar'
import VisualizationsBasicLineChart from '../visualization-charts/VisualizationsBasicLineChart'
import IntelDataGrid from '../../data-grid/IntelDataGrid'
import { networkAttackColumns } from '../NetworkAttackColumns'
import VisualizationsBasicHeatmap from '../visualization-charts/VisualizationsBasicHeatmap'
import { useAppDispatch } from '../../../reducers/store'
import { useSelector } from 'react-redux'
import { fetchThreatIntelNetwork, selectAdversaries } from '../../../reducers/adversaries'
import { useEffect, useState } from 'react'
import {
  mapIntelForAttackVolumeCountries,
  mapIntelToAttackByThreatEvent,
  mapIntelToAttackVolumeByCountryPerMonth,
  mapIntelToNetworkAttacksMap,
  mapIntelToNetworkThreatsLineChart,
  mapIntelToSumOfAttacksHeatmap,
  mapIntelToThreatTypeByCountryHeatmap,
  mapIntelToThreatTypesTrendLineChart,
} from '../../../utils/network-mapper'
import { AttackByThreatTypeByCountry, AttacksHeatmapMapperData, BarChart, LineChart } from '../../../types/StatTypes'
import { Tooltip } from '@radix-ui/themes'

interface IVisualizationNetworkPageProps {
  mapData: object
}

function VisualizationNetworkPage(props: IVisualizationNetworkPageProps) {
  const { mapData } = props
  const di = useAppDispatch()
  const { networkData } = useSelector(selectAdversaries)

  const [intelForNetworkAttacksByCountry, setIntelForNetworkAttacksByCountry] = useState<string>('')
  const [intelForThreatsByThreatType, setIntelForThreatsByThreatType] = useState<BarChart>([])
  const [intelForThreatsByNetworkType, setIntelForThreatsByNetworkType] = useState<LineChart>({
    categories: [],
    series: [],
  })
  const [dataForGrid, setDataForGrid] = useState<AttackByThreatTypeByCountry[]>([])
  const [intelForThreatsByTrendType, setIntelForThreatsByTrendType] = useState<LineChart>({
    categories: [],
    series: [],
  })
  const [intelForSumOfAttackByMonth, setIntelForSumOfAttackByMonth] = useState<AttacksHeatmapMapperData>({
    yAxis: [],
    xAxis: [],
    heatmapData: [],
  })
  const [intelForAttackVolumeCountries, setIntelForAttackVolumeCountries] = useState<BarChart>([])
  const [intelForAttackVolumeCountriesByMonth, setIntelForAttackVolumeCountriesByMonth] =
    useState<AttacksHeatmapMapperData>({
      yAxis: [],
      xAxis: [],
      heatmapData: [],
    })
  const [intelForThreatTypeByCountryHeatmap, setIntelForThreatTypeByCountryHeatmap] =
    useState<AttacksHeatmapMapperData>({
      yAxis: [],
      xAxis: [],
      heatmapData: [],
    })

  useEffect(() => {
    di(fetchThreatIntelNetwork())
  }, [])

  useEffect(() => {
    if (networkData) {
      setIntelForNetworkAttacksByCountry(mapIntelToNetworkAttacksMap(networkData.networkAttacksByCountry))
      setIntelForThreatsByThreatType(mapIntelToAttackByThreatEvent(networkData.networkAttackByThreatEvents))
      setIntelForThreatsByNetworkType(mapIntelToNetworkThreatsLineChart(networkData.networkAttackByThreatTypeByMonth))
      setDataForGrid(networkData.attackByThreatTypeByCountry)
      setIntelForThreatsByTrendType(mapIntelToThreatTypesTrendLineChart(networkData.threatTypeTrend))
      setIntelForSumOfAttackByMonth(mapIntelToSumOfAttacksHeatmap(networkData.sumOfAttacksByMonth))
      setIntelForAttackVolumeCountries(mapIntelForAttackVolumeCountries(networkData.attackVolumeByCountry))
      setIntelForAttackVolumeCountriesByMonth(
        mapIntelToAttackVolumeByCountryPerMonth(networkData.attackVolumeByCountryByMonth)
      )
      setIntelForThreatTypeByCountryHeatmap(
        mapIntelToThreatTypeByCountryHeatmap(networkData.attackByThreatTypeByCountry)
      )
    }
  }, [networkData])

  return (
    <div className="bg-contentBackground h-full max-w-[2000px] mx-auto">
      <div className="pb-9">
        <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
          <div className="flex">
            <div className="text-mainText text-lg leading-relaxed font-medium">Network Attacks by Target Country</div>
            <Tooltip
              content={`Hover over a country to see the number of network attacks it has received. Analyze global threat patterns and identify hotspots of activity.`}
              side="top"
              align="end"
              className="max-w-[400px]"
            >
              <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
            </Tooltip>
          </div>
          <VisualizationsBasicMap
            chartData={intelForNetworkAttacksByCountry}
            mapData={mapData}
            tooltipTitle={'Network Attacks'}
          />
        </div>

        <div className="grid grid-cols-12 gap-4 h-auto mb-4">
          <div className="col-span-12 md:col-span-6">
            <div className="bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">
                  Network Attack Types by Attack Volume
                </div>
                <Tooltip
                  content={`View the distribution of attack types and their associated volume. Hover over each bar to see the total number of occurrences.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicBar
                chartData={intelForThreatsByThreatType}
                tooltipTitle={'Attacks'}
                long
                biggerLabels
              />
            </div>

            <div className="flex-grow bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">Network Attacks by Network Type</div>
                <Tooltip
                  content={`Compare attack trends across 3G and 4G networks. Track fluctuations and identify patterns in attack frequency over time.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicLineChart chartData={intelForThreatsByNetworkType} height="835px" />
            </div>

            <div className="flex-grow bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">Network Attacks by Attack Type</div>
                <Tooltip
                  content={`Track the evolution of different attack types over the past two years. Hover over the lines to see the total number of attacks at any given point in time.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicLineChart chartData={intelForThreatsByTrendType} height="842px" />
            </div>

            <div className="flex-grow bg-cardFill rounded-md border border-mainBorder px-4 py-2">
              <div className="flex">
                <div className="text-mainText text-lg leading-relaxed font-medium">Network Attacks per Month</div>
                <Tooltip
                  content={`Visualize attack frequency over time. Each cell represents the number of attacks per month, helping you spot trends and seasonal patterns.`}
                  side="top"
                  align="end"
                  className="max-w-[400px]"
                >
                  <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
                </Tooltip>
              </div>
              <VisualizationsBasicHeatmap chartData={intelForSumOfAttackByMonth} />
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 bg-cardFill rounded-md border border-mainBorder px-4 py-2">
            <div className="flex">
              <div className="text-mainText text-lg leading-relaxed font-medium">
                Network Attacks per Target Country and Threat Type
              </div>
              <Tooltip
                content={`Identify global hotspots of targeted activity. Each country is mapped against different threat types, with color intensity indicating the level of attacks received.`}
                side="top"
                align="end"
                className="max-w-[400px]"
              >
                <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
              </Tooltip>
            </div>
            <VisualizationsBasicHeatmap chartData={intelForThreatTypeByCountryHeatmap} height={'3100px'} logarithmic />
          </div>
        </div>

        {/* GRID */}
        <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder py-2 mb-4 min-h-[480px]">
          <div className="flex px-4">
            <div className="text-mainText text-lg leading-relaxed font-medium">Network Attack Source Summary</div>
            <Tooltip
              content={`Breakdown of attack sources. View the country of origin, source, network type, threat type, and attack volume to analyze threat sources.`}
              side="top"
              align="end"
              className="max-w-[400px]"
            >
              <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
            </Tooltip>
          </div>
          <IntelDataGrid columns={networkAttackColumns()} data={dataForGrid} loading={false} />
        </div>

        <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
          <div className="flex">
            <div className="text-mainText text-lg leading-relaxed font-medium">
              Network Attacks per Target Country (Past 12 Months)
            </div>
            <Tooltip
              content={`See which countries have been attacked the most over the past 12 months. Each bar represents the total number of attacks a country has received.`}
              side="top"
              align="end"
              className="max-w-[400px]"
            >
              <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
            </Tooltip>
          </div>
          <VisualizationsBasicBar chartData={intelForAttackVolumeCountries} tooltipTitle="Attacks" />
        </div>

        <div className="grid grid-cols-1 gap-4 h-auto bg-cardFill rounded-md border border-mainBorder px-4 py-2 mb-4 min-h-[400px]">
          <div className="flex">
            <div className="text-mainText text-lg leading-relaxed font-medium">
              Network Attacks per Source Country (Past 10 Months)
            </div>
            <Tooltip
              content={`Track the number of attacks carried out by each country over the past 10 months. Hover over each month to see detailed attack activity by country for each month.`}
              side="top"
              align="end"
              className="max-w-[400px]"
            >
              <IconInfoCircle size={18} className="text-secondaryText mt-1.5 ml-2 cursor-pointer" />
            </Tooltip>
          </div>
          <VisualizationsBasicHeatmap chartData={intelForAttackVolumeCountriesByMonth} height={'2400px'} logarithmic />
        </div>
      </div>
    </div>
  )
}

export default VisualizationNetworkPage
